import { mergeStyles } from '@cian/utils/lib/shared/style';
import * as PropTypes from 'prop-types';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { IOfferSchema, IUserSchema } from '@cian/api-models/offer-card/v1/get-offer-data';
import { ErrorLogComponent } from '@cian/error-log-component';
import { ICountrysideBuilderLandingPromoVillageData } from '../../types';

import { addAdFoxBanner } from '../adfox';
import { CommentsButtonContainer } from '../comments/button/container';
import { CommentContainer } from '../comments/comment/container';
import { IContext } from '../../store';
import { getRandomId } from '../../utils/banners';
import { ExportButtonContainer } from '../export_button/container';
import { FavoriteButtonContainer } from '../favorite_button/container';
import { GeoContainer } from '../geo/container';
import { HeaderCountrysideBuilderPromo } from '../HeaderCountrysideBuilderPromo';
import { OfferTitleContainer } from '../offer_title/container';
import { ParentContainer } from '../parent/container';
import { SharingContainer } from '../sharing/container';
import { StretchBanner } from '../stretch_banner/index';
import { ComplaintButtonContainer } from './complaint_button/container';
import * as styles from './index.css';
import { PrintButtonContainer } from './print_button/container';
import { HideButtonContainer } from '../hide_button/container';
import { getDealType, isRent } from '../../utils/deal_type';
import { getSpecialRentPriceTargetPuidForAdFox } from '../../utils/adfox';
import { isSuburbanOfferFromBuilder } from '../../utils/suburban';

const PR1_RANDOM = getRandomId();
const PR2_RANDOM = getRandomId();

export interface IMainProps {
  offer: IOfferSchema;
  user: IUserSchema | null;
  isOpenCommentBlock: boolean;
  isCountrysideCardIntegrated: boolean;
  countrysideBuilderLandingPromoData?: ICountrysideBuilderLandingPromoVillageData | null;
}

export class Main extends React.PureComponent<IMainProps, {}> {
  public context: IContext;

  public static contextTypes = {
    config: PropTypes.object,
    logger: PropTypes.object,
    makeRequest: PropTypes.func,
    telemetry: PropTypes.object,
  };

  public componentDidMount() {
    const { user } = this.props;

    const stretchBannerDomElement = document.getElementById('stretch-banner');

    if (stretchBannerDomElement) {
      ReactDOM.render(
        <StretchBanner offer={this.props.offer} makeRequest={this.context.makeRequest} user={user} />,
        stretchBannerDomElement,
      );
    }
  }

  public render() {
    const { offer, user, isCountrysideCardIntegrated, countrysideBuilderLandingPromoData } = this.props;
    const dealType = getDealType(offer);

    const isFirstCountrysideBannerVisible =
      !!countrysideBuilderLandingPromoData && countrysideBuilderLandingPromoData.bannerType === 'banner1';

    return (
      <section className={styles['main']}>
        <div className={styles['header']}>
          <div
            {...mergeStyles(
              styles['header-information'],
              isFirstCountrysideBannerVisible && styles['header-information--with-promo'],
            )}
          >
            <OfferTitleContainer />
            {!isCountrysideCardIntegrated && <ParentContainer />}
            <GeoContainer />
            {offer.status === 'published' && this.renderToolbar()}
            {isFirstCountrysideBannerVisible && (
              <HeaderCountrysideBuilderPromo
                data={countrysideBuilderLandingPromoData! /*tslint:disable-line:no-non-null-assertion */}
                offerId={offer.id}
              />
            )}
          </div>
          {!isSuburbanOfferFromBuilder(offer) && (
            <div className={styles['mortgage-banner']}>
              {!isRent(dealType) &&
                addAdFoxBanner({
                  type: 'offer_card_image_banner',
                  key: 'offer_card_image_banner',
                  makeRequest: this.context.makeRequest,
                  offer,
                  randomId: PR1_RANDOM,
                  positionInfo: 'offer_card_mortgage-1',
                  isAgent: !!user && !!user.isAgent,
                  additionalPuids: { puid27: this.getPuidAreaTarget() },
                })}
              {isRent(dealType) &&
                addAdFoxBanner({
                  type: 'mortgage_in_rent',
                  key: 'mortgage_in_rent',
                  makeRequest: this.context.makeRequest,
                  offer,
                  randomId: PR2_RANDOM,
                  positionInfo: 'mortgage_in_rent-1',
                  isAgent: !!user && !!user.isAgent,
                  additionalPuids: {
                    puid27: this.getPuidAreaTarget(),
                    puid33: getSpecialRentPriceTargetPuidForAdFox(offer.bargainTerms.price),
                  },
                })}
            </div>
          )}
        </div>
        {this.props.isOpenCommentBlock && (
          <ErrorLogComponent logger={this.context.logger}>
            <CommentContainer />
          </ErrorLogComponent>
        )}
      </section>
    );
  }

  private renderToolbar() {
    return (
      <div {...mergeStyles(styles['tools-container'], this.props.isOpenCommentBlock && styles['width_comment'])}>
        <div className={styles['tool']}>
          <FavoriteButtonContainer />
        </div>
        <div className={styles['tool']}>
          <SharingContainer />
        </div>
        <div className={styles['tool']}>
          <CommentsButtonContainer />
        </div>
        <div className={styles['tool']}>
          <ExportButtonContainer />
        </div>
        <PrintButtonContainer className={styles['tool']} />
        <HideButtonContainer className={styles['tool']} />
        <ComplaintButtonContainer className={styles['tool']} />
      </div>
    );
  }

  private getPuidAreaTarget = () => {
    const { offer } = this.props;
    const { totalArea } = offer;

    if (totalArea) {
      const area = Number(totalArea);

      if (area >= 10 && area < 20) {
        return '10_20';
      } else if (area >= 20 && area < 40) {
        return '20_40';
      } else if (area >= 40 && area < 65) {
        return '40_65';
      } else if (area >= 65 && area < 80) {
        return '65_80';
      } else if (area >= 80 && area < 100) {
        return '80_100';
      } else if (area >= 100) {
        return '100_max';
      }
    }

    return undefined;
  };
}
